import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { apiErrorToast } from '@/lib/apiErrorToast';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Bell, CheckCircle, XCircle, AlertTriangle, Users, IndianRupee, CalendarDays,
  TrendingUp, ClipboardCheck, CreditCard, MessageSquare, Shield, FileText,
  Trophy, Clock, UserPlus, Check, Search, Filter, Inbox, Archive, Trash2, Send, Megaphone
} from 'lucide-react';
import { toast } from 'sonner';

const iconMap = {
  'alert-triangle': AlertTriangle, 'clipboard-check': ClipboardCheck,
  'indian-rupee': IndianRupee, 'check-circle': CheckCircle, 'user-plus': UserPlus,
  'users': Users, 'calendar-days': CalendarDays, 'trending-up': TrendingUp,
  'credit-card': CreditCard, 'message-square': MessageSquare, 'x-circle': XCircle,
  'clock': Clock, 'trophy': Trophy, 'shield': Shield, 'file-text': FileText, 'bell': Bell,
};

const priorityConfig = {
  high: { label: 'Urgent', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', dot: 'bg-red-500', border: 'border-l-red-500' },
  medium: { label: 'Important', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', dot: 'bg-amber-500', border: 'border-l-amber-500' },
  low: { label: 'Info', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', dot: 'bg-blue-500', border: 'border-l-blue-500' },
};

export default function NotificationsInbox() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showCreateAnnouncement, setShowCreateAnnouncement] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    message: '',
    targetType: 'all',
    targetValue: '',
    sendNotification: true,
    sendWhatsApp: false
  });
  const [sending, setSending] = useState(false);
  const [centers, setCenters] = useState([]);
  const [twilioConfigured, setTwilioConfigured] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [nRes, sRes, settingsRes, centersRes] = await Promise.all([
        api.get('/notifications', { params: { limit: 50 } }),
        api.get('/notifications/stats'),
        api.get('/settings').catch(() => ({ data: {} })),
        api.get('/centers').catch(() => ({ data: [] }))
      ]);
      const data = nRes.data;
      setNotifications(data.notifications || (Array.isArray(data) ? data : []));
      setStats(sRes.data);
      
      // Check if Twilio is configured
      const settings = settingsRes.data || {};
      const twilioConfigured = !!(settings.twilio_account_sid && settings.twilio_auth_token && settings.twilio_phone_number);
      setTwilioConfigured(twilioConfigured);
      
      // Set centers
      setCenters(centersRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMarkRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (err) { apiErrorToast(err, 'Failed to mark as read'); }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch (err) { apiErrorToast(err, 'Failed to mark all as read'); }
  };

  const handleSeed = async () => {
    try {
      const res = await api.post('/notifications/seed');
      toast.success(res.data.message);
      fetchData();
    } catch (err) { apiErrorToast(err, 'Failed to generate sample notifications'); }
  };


  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    if (!announcementForm.title || !announcementForm.message) {
      toast.error('Please fill in title and message');
      return;
    }
    
    setSending(true);
    try {
      const res = await api.post('/announcements/send', {
        title: announcementForm.title,
        message: announcementForm.message,
        target_type: announcementForm.targetType,
        target_value: announcementForm.targetValue,
        channels: {
          notification: announcementForm.sendNotification,
          whatsapp: announcementForm.sendWhatsApp
        }
      });
      
      toast.success(`Announcement sent to ${res.data.sent_count} recipient(s)!`);
      setShowCreateAnnouncement(false);
      setAnnouncementForm({
        title: '',
        message: '',
        targetType: 'all',
        targetValue: '',
        sendNotification: true,
        sendWhatsApp: false
      });
      fetchData(); // Refresh to show new announcements
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setSending(false);
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  // Apply filters
  const filtered = notifications.filter(n => {
    if (filter === 'unread' && n.read) return false;
    if (filter === 'read' && !n.read) return false;
    if (priorityFilter !== 'all' && n.priority !== priorityFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (n.message || '').toLowerCase().includes(q) || (n.title || '').toLowerCase().includes(q) || (n.type || '').toLowerCase().includes(q);
    }
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;
  const highCount = notifications.filter(n => !n.read && n.priority === 'high').length;

  if (loading) return (
    <div className="space-y-4" data-testid="notifications-loading">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      <Skeleton className="h-96 rounded-xl" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="notifications-inbox-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-primary" /> Broadcast
          </h1>
          <p className="text-sm text-muted-foreground">{unreadCount} unread{highCount > 0 ? ` • ${highCount} urgent` : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
              <Check className="h-4 w-4 mr-1" /> Mark All Read
            </Button>
          )}
          {/* Phase 202.bugfix — `can_broadcast` is server-derived (true for
              academy_owner, super_admin AND employees with `manager`
              designation or "Manager" tag). See `_user_can_broadcast` in
              server.py. The designation/tags fallbacks below are a
              defensive client-side guard for any cached/stale auth payload
              where `can_broadcast` may not yet be hydrated. */}
          {(
            user?.can_broadcast ||
            user?.role === 'academy_owner' ||
            user?.role === 'super_admin' ||
            (user?.designation || '').toLowerCase() === 'manager' ||
            (Array.isArray(user?.tags) && user.tags.some(t => (t || '').toLowerCase() === 'manager')) ||
            (Array.isArray(user?.role_tags) && user.role_tags.some(t => (t || '').toLowerCase() === 'manager'))
          ) && (
            <Button size="sm" onClick={() => setShowCreateAnnouncement(true)} data-testid="create-announcement-btn" className="bg-primary">
              <Send className="h-4 w-4 mr-1" /> Create Broadcast
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('all')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">Total</span>
                <Bell className="h-4 w-4 text-slate-500" />
              </div>
              <p className="text-2xl font-display font-bold tabular-nums">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('unread')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">Unread</span>
                <span className="h-3 w-3 rounded-full bg-primary" />
              </div>
              <p className="text-2xl font-display font-bold tabular-nums text-primary">{stats.unread}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setPriorityFilter('high')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">Urgent</span>
                <AlertTriangle className="h-4 w-4 text-red-500" />
              </div>
              <p className="text-2xl font-display font-bold tabular-nums text-red-600">{stats.by_priority?.high || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">Types</span>
                <Filter className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-display font-bold tabular-nums">{Object.keys(stats.by_type || {}).length}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search notifications..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="notif-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-28 h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="unread">Unread</SelectItem>
            <SelectItem value="read">Read</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-32 h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            <SelectItem value="high">Urgent</SelectItem>
            <SelectItem value="medium">Important</SelectItem>
            <SelectItem value="low">Info</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="secondary" className="text-xs tabular-nums">{filtered.length} results</Badge>
      </div>

      {/* Notification List */}
      <Card>
        <CardContent className="p-0 divide-y divide-border">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Inbox className="h-12 w-12 text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-semibold mb-1">No notifications</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {filter === 'unread' ? 'All caught up!' : search ? 'Try different keywords' : 'Click "Generate Sample" to see notifications'}
              </p>
              {notifications.length === 0 && (
                <Button variant="outline" size="sm" onClick={handleSeed}>Generate Sample Notifications</Button>
              )}
            </div>
          ) : filtered.map(notif => {
            const IconComp = iconMap[notif.icon] || iconMap[notif.type] || Bell;
            const pCfg = priorityConfig[notif.priority] || priorityConfig.low;
            return (
              <div
                key={notif.id}
                className={`flex gap-4 px-4 sm:px-5 py-4 transition-colors duration-150 border-l-4 ${!notif.read ? `bg-primary/[0.03] ${pCfg.border}` : 'border-l-transparent hover:bg-muted/30'}`}
                data-testid={`notif-inbox-${notif.id}`}
              >
                {/* Icon */}
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${pCfg.color}`}>
                  <IconComp className="h-5 w-5" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className={`text-sm leading-snug ${!notif.read ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                        {notif.message || notif.title}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="text-xs text-muted-foreground">{timeAgo(notif.created_at)}</span>
                        <span className="text-xs text-muted-foreground">•</span>
                        <Badge variant="outline" className="text-[10px] h-5 capitalize">{(notif.type || 'general').replace(/_/g, ' ')}</Badge>
                        {/* Phase 72 — flag broadcasts originating from the Super Admin so academies know it's not their own internal announcement */}
                        {(notif.data?.scope === 'system_wide' || notif.data?.source === 'announcement_composer') && (
                          <Badge variant="outline" className="text-[10px] h-5 border-indigo-400 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/20 dark:text-indigo-300" data-testid="system-wide-badge">
                            System-Wide
                          </Badge>
                        )}
                        {notif.priority === 'high' && <Badge variant="destructive" className="text-[10px] h-5">Urgent</Badge>}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!notif.read && (
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => handleMarkRead(notif.id)} data-testid="mark-read-btn">
                          <Check className="h-3 w-3 mr-0.5" /> Read
                        </Button>
                      )}
                      {!notif.read && <span className={`h-2.5 w-2.5 rounded-full ${pCfg.dot}`} />}
                    </div>
                  </div>


      {/* Create Announcement Dialog - Improved Responsiveness */}
      <Dialog open={showCreateAnnouncement} onOpenChange={setShowCreateAnnouncement}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-primary" />
              Create Announcement
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateAnnouncement} className="space-y-4">
            {/* Title */}
            <div className="space-y-2">
              <Label className="text-sm">Announcement Title *</Label>
              <Input 
                value={announcementForm.title}
                onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                placeholder="e.g., Important: Training Session Cancelled"
                required
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label className="text-sm">Message *</Label>
              <Textarea 
                value={announcementForm.message}
                onChange={(e) => setAnnouncementForm({...announcementForm, message: e.target.value})}
                placeholder="Write your announcement message here..."
                rows={4}
                required
              />
            </div>

            {/* Audience Targeting */}
            <div className="space-y-2">
              <Label className="text-sm">Send To</Label>
              <Select value={announcementForm.targetType} onValueChange={(v) => setAnnouncementForm({...announcementForm, targetType: v, targetValue: ''})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select audience" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All (Students + Employees)</SelectItem>
                  <SelectItem value="all_students">All Students</SelectItem>
                  <SelectItem value="all_employees">All Employees</SelectItem>
                  <SelectItem value="batch">Specific Batch</SelectItem>
                  <SelectItem value="course">Specific Course</SelectItem>
                  <SelectItem value="department">Specific Department</SelectItem>
                  <SelectItem value="center">Specific Center</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Conditional dropdown for specific targeting */}
            {announcementForm.targetType === 'batch' && (
              <div className="space-y-2">
                <Label className="text-sm">Select Batch</Label>
                <Select value={announcementForm.targetValue} onValueChange={(v) => setAnnouncementForm({...announcementForm, targetValue: v})}>
                  <SelectTrigger><SelectValue placeholder="Choose batch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Morning">Morning</SelectItem>
                    <SelectItem value="Afternoon">Afternoon</SelectItem>
                    <SelectItem value="Evening">Evening</SelectItem>
                    <SelectItem value="Weekend">Weekend</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {announcementForm.targetType === 'course' && (
              <div className="space-y-2">
                <Label className="text-sm">Select Course</Label>
                <Select value={announcementForm.targetValue} onValueChange={(v) => setAnnouncementForm({...announcementForm, targetValue: v})}>
                  <SelectTrigger><SelectValue placeholder="Choose course" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Football">Football</SelectItem>
                    <SelectItem value="Cricket">Cricket</SelectItem>
                    <SelectItem value="Badminton">Badminton</SelectItem>
                    <SelectItem value="Swimming">Swimming</SelectItem>
                    <SelectItem value="Tennis">Tennis</SelectItem>
                    <SelectItem value="Basketball">Basketball</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {announcementForm.targetType === 'department' && (
              <div className="space-y-2">
                <Label className="text-sm">Select Department</Label>
                <Select value={announcementForm.targetValue} onValueChange={(v) => setAnnouncementForm({...announcementForm, targetValue: v})}>
                  <SelectTrigger><SelectValue placeholder="Choose department" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Coaching">Coaching</SelectItem>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="Support">Support</SelectItem>
                    <SelectItem value="Management">Management</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {announcementForm.targetType === 'center' && (
              <div className="space-y-2">
                <Label className="text-sm">Select Center</Label>
                {centers.length > 0 ? (
                  <Select value={announcementForm.targetValue} onValueChange={(v) => setAnnouncementForm({...announcementForm, targetValue: v})}>
                    <SelectTrigger><SelectValue placeholder="Choose center" /></SelectTrigger>
                    <SelectContent>
                      {centers.map(center => (
                        <SelectItem key={center.id} value={center.name}>{center.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input 
                    value={announcementForm.targetValue}
                    onChange={(e) => setAnnouncementForm({...announcementForm, targetValue: e.target.value})}
                    placeholder="Enter center name (or add centers in Settings)"
                  />
                )}
              </div>
            )}

            {/* Delivery Channels */}
            <div className="space-y-3">
              <Label className="text-sm">Delivery Channels</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={announcementForm.sendNotification}
                    onCheckedChange={(checked) => setAnnouncementForm({...announcementForm, sendNotification: checked})}
                  />
                  <Label className="text-sm font-normal cursor-pointer">Send In-App Notification</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={announcementForm.sendWhatsApp}
                    onCheckedChange={(checked) => setAnnouncementForm({...announcementForm, sendWhatsApp: checked})}
                    disabled={!twilioConfigured}
                  />
                  <Label className="text-sm font-normal cursor-pointer">Send WhatsApp Message</Label>
                  {!twilioConfigured && (
                    <Badge variant="outline" className="text-xs text-amber-600">Configure Twilio in Settings</Badge>
                  )}
                  {twilioConfigured && (
                    <Badge className="text-xs bg-emerald-100 text-emerald-700 border-0">✓ Configured</Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="p-4 rounded-lg bg-muted/50 border">
              <p className="text-xs text-muted-foreground mb-2">Preview:</p>
              <p className="text-sm font-semibold">{announcementForm.title || 'Announcement title...'}</p>
              <p className="text-sm text-muted-foreground mt-1">{announcementForm.message || 'Message content...'}</p>
              <p className="text-xs text-muted-foreground mt-2">
                Audience: <strong>{announcementForm.targetType === 'all' ? 'All Students & Employees' : 
                  announcementForm.targetType === 'all_students' ? 'All Students' :
                  announcementForm.targetType === 'all_employees' ? 'All Employees' :
                  `${announcementForm.targetType}: ${announcementForm.targetValue || 'Select...'}`}</strong>
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowCreateAnnouncement(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={sending}>
                {sending ? (
                  <>
                    <Clock className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Announcement
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
